/**
 * Fix MUI overlay positioning under `body { zoom }` (app 80% scale / navbar A controls).
 *
 * Forms (NewLeadModal, NewAccountForm, NewContactModal, …) and navbar Menus
 * (profile, notifications) use MUI Select / Menu / Autocomplete / Tooltip.
 * Under CSS zoom they drift because:
 *
 *   Select / Menu  → Popover → style.top / style.left
 *   Autocomplete / Tooltip → Popper → style.transform (translate3d)
 *
 * getBoundingClientRect() is in visual px; those style values are CSS px that
 * zoom then scales again. Divide by getCssZoom() when the values are written.
 *
 * React often writes via style.setProperty() (not the `top`/`left` setters), so
 * we patch both paths and re-sync on MutationObserver style changes.
 */

import { getCssZoom } from "assets/theme/base/appScale";

/** @deprecated Prefer installCssZoomOverlayFix — kept so theme imports do not break. */
export const cssZoomPopperModifiers = [];

const zoomFixStyles = new WeakSet();
/** @type {WeakMap<CSSStyleDeclaration, HTMLElement>} */
const styleToElement = new WeakMap();

const TRANSLATE_3D = /translate3d\(\s*(-?[\d.]+)px\s*,\s*(-?[\d.]+)px\s*,\s*(-?[\d.]+)px\s*\)/;
const TRANSLATE = /translate\(\s*(-?[\d.]+)px\s*,\s*(-?[\d.]+)px\s*\)/;

function scaleCssPx(value) {
  const zoom = getCssZoom();
  if (zoom === 1 || value == null || value === "") return value;
  const raw = parseFloat(value);
  if (!Number.isFinite(raw)) return value;
  return `${Math.round(raw / zoom)}px`;
}

function getStyleDescriptor(prop) {
  let proto = CSSStyleDeclaration.prototype;
  while (proto && proto !== Object.prototype) {
    const desc = Object.getOwnPropertyDescriptor(proto, prop);
    if (desc?.set && desc?.get) return desc;
    proto = Object.getPrototypeOf(proto);
  }
  return null;
}

function markCorrected(style, prop, value) {
  const el = styleToElement.get(style);
  if (!el || value == null) return;
  if (prop === "top") el.dataset.pearlZoomTop = String(value);
  if (prop === "left") el.dataset.pearlZoomLeft = String(value);
}

let prototypePatched = false;

function patchCssStyleDeclarationPrototype() {
  if (prototypePatched || typeof CSSStyleDeclaration === "undefined") return;
  prototypePatched = true;

  ["top", "left"].forEach((prop) => {
    const desc = getStyleDescriptor(prop);
    if (!desc) return;

    Object.defineProperty(CSSStyleDeclaration.prototype, prop, {
      configurable: true,
      enumerable: true,
      get() {
        return desc.get.call(this);
      },
      set(value) {
        if (zoomFixStyles.has(this)) {
          value = scaleCssPx(value);
          markCorrected(this, prop, value);
        }
        desc.set.call(this, value);
      },
    });
  });

  // React / Emotion often set position via setProperty, which bypasses the
  // property setters above — without this, navbar Menus stay misaligned.
  const originalSetProperty = CSSStyleDeclaration.prototype.setProperty;
  CSSStyleDeclaration.prototype.setProperty = function setProperty(property, value, priority) {
    const prop = String(property || "").toLowerCase();
    if (zoomFixStyles.has(this) && (prop === "top" || prop === "left") && value) {
      value = scaleCssPx(value);
      markCorrected(this, prop, value);
    }
    if (priority === undefined) {
      return originalSetProperty.call(this, property, value);
    }
    return originalSetProperty.call(this, property, value, priority);
  };
}

/**
 * Re-read inline top/left and correct if they are still unscaled MUI values.
 * Uses data attributes so our own writes do not get divided twice.
 */
function syncPaperPosition(paper) {
  if (!paper?.style) return;
  const zoom = getCssZoom();
  if (zoom === 1) return;

  const topDesc = getStyleDescriptor("top");
  const leftDesc = getStyleDescriptor("left");
  const top = topDesc?.get?.call(paper.style) || "";
  const left = leftDesc?.get?.call(paper.style) || "";

  if (top && paper.dataset.pearlZoomTop !== top) {
    const fixed = scaleCssPx(top);
    topDesc.set.call(paper.style, fixed);
    paper.dataset.pearlZoomTop = fixed;
  }
  if (left && paper.dataset.pearlZoomLeft !== left) {
    const fixed = scaleCssPx(left);
    leftDesc.set.call(paper.style, fixed);
    paper.dataset.pearlZoomLeft = fixed;
  }
}

/**
 * Register a Popover/Menu paper so subsequent top/left writes are zoom-corrected.
 * Safe to call repeatedly (e.g. Menu TransitionProps.onEntered).
 */
export function wrapPaperPositioning(paper) {
  if (!paper?.style) return;
  // In-flow menus (navbar profile) must not be rewritten by the portal zoom math.
  if (paper.dataset?.pearlZoomSkip === "1") return;

  patchCssStyleDeclarationPrototype();
  styleToElement.set(paper.style, paper);

  if (!zoomFixStyles.has(paper.style)) {
    zoomFixStyles.add(paper.style);
  }

  syncPaperPosition(paper);
}

function scaleTranslateValue(transform) {
  const zoom = getCssZoom();
  if (zoom === 1 || !transform) return null;

  if (TRANSLATE_3D.test(transform)) {
    return transform.replace(TRANSLATE_3D, (_, x, y, z) => {
      const nx = Math.round(parseFloat(x) / zoom);
      const ny = Math.round(parseFloat(y) / zoom);
      return `translate3d(${nx}px, ${ny}px, ${z}px)`;
    });
  }

  if (TRANSLATE.test(transform)) {
    return transform.replace(TRANSLATE, (_, x, y) => {
      const nx = Math.round(parseFloat(x) / zoom);
      const ny = Math.round(parseFloat(y) / zoom);
      return `translate(${nx}px, ${ny}px)`;
    });
  }

  return null;
}

function correctPopperTransform(el) {
  if (!el?.style) return;
  const transform = el.style.transform;
  if (!transform) return;
  if (el.dataset.pearlZoomTx === transform) return;

  const next = scaleTranslateValue(transform);
  if (!next || next === transform) return;

  el.dataset.pearlZoomTx = next;
  el.style.transform = next;
}

const PAPER_SELECTOR = ".MuiPopover-paper, .MuiMenu-paper";
const POPPER_SELECTOR =
  "[data-popper-placement], .MuiPopper-root, .MuiAutocomplete-popper, .MuiTooltip-popper";

function scanNode(root) {
  if (!root || root.nodeType !== 1) return;

  if (root.matches?.(PAPER_SELECTOR)) wrapPaperPositioning(root);
  root.querySelectorAll?.(PAPER_SELECTOR).forEach(wrapPaperPositioning);

  if (root.matches?.(POPPER_SELECTOR)) correctPopperTransform(root);
  root.querySelectorAll?.(POPPER_SELECTOR).forEach(correctPopperTransform);
}

/**
 * Install once at app boot. Covers every Select / Autocomplete / Tooltip / Menu
 * including the navbar profile and notification menus under zoom-out.
 */
export function installCssZoomOverlayFix() {
  if (typeof document === "undefined") return;
  if (document.documentElement.dataset.pearlZoomOverlayFix === "1") return;
  document.documentElement.dataset.pearlZoomOverlayFix = "1";

  patchCssStyleDeclarationPrototype();

  const start = () => {
    const root = document.body;
    if (!root) return;

    scanNode(root);

    const mo = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        if (mutation.type === "childList") {
          mutation.addedNodes.forEach(scanNode);
        } else if (
          mutation.type === "attributes" &&
          mutation.attributeName === "style" &&
          mutation.target?.nodeType === 1
        ) {
          const el = mutation.target;
          if (el.matches?.(PAPER_SELECTOR)) {
            // Always re-sync: setProperty / cssText can change top/left after wrap.
            wrapPaperPositioning(el);
          }
          if (el.matches?.(POPPER_SELECTOR) || el.hasAttribute?.("data-popper-placement")) {
            correctPopperTransform(el);
          }
        }
      }
    });

    mo.observe(root, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ["style", "data-popper-placement"],
    });
  };

  if (document.body) {
    start();
  } else {
    document.addEventListener("DOMContentLoaded", start, { once: true });
  }
}

export default cssZoomPopperModifiers;
